export interface InViewportConfigOptions {
  root?: Element;
  threshold?: number | number[];
  timeThreshold?: number;
}

/**
 *  Set default threshold on 75% as requested by analytics team
 */
export const DEFAULT_IMPRESSION_THRESHOLD = 0.75;

/**
 * Default time (in milliseconds) that an element must be in viewport
 * before triggering the impression event
 */
export const DEFAULT_IMPRESSION_TIME_THRESHOLD = 1000;

/**
 * More info https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Intersection_observer_options
 */
const DEFAULT_INTERSECTION_THRESHOLD = [DEFAULT_IMPRESSION_THRESHOLD];

export class InViewportConfig {
  private _root?: Element;
  public readonly rootMargin = '0px 0px 0px 0px';
  private _threshold: number | number[] = [...DEFAULT_INTERSECTION_THRESHOLD];
  private readonly _timeThreshold: number = DEFAULT_IMPRESSION_TIME_THRESHOLD;
  private _inViewportTimer?: ReturnType<typeof setTimeout>;
  private _isInViewport = false;

  constructor(options?: InViewportConfigOptions) {
    if (options == null) {
      return;
    }
    this.root = options.root;
    this.threshold = options.threshold;
    if (options.timeThreshold !== undefined) {
      this._timeThreshold = options.timeThreshold;
    }
  }

  /**
   * Gets the configured time threshold in milliseconds
   */
  public get timeThreshold(): number {
    return this._timeThreshold;
  }

  /**
   * Call when the element enters the viewport.
   * Starts the impression timer.
   */
  public onViewportTimerTresholdPassed(callback: () => void): void {
    if (!this._isInViewport) {
      this._isInViewport = true;
      this.clearInViewportTimer();
      this._inViewportTimer = setTimeout(() => {
        callback();
      }, this._timeThreshold);
    }
  }

  /**
   * Call when the element leaves the viewport.
   * Clears the impression timer.
   */
  public clearViewportTimer(): void {
    if (this._isInViewport) {
      this._isInViewport = false;
      this.clearInViewportTimer();
    }
  }

  /**
   * Clears any active viewport timer
   */
  private clearInViewportTimer(): void {
    if (this._inViewportTimer) {
      clearTimeout(this._inViewportTimer);
      this._inViewportTimer = undefined;
    }
  }

  public get root(): Element | undefined {
    return this._root;
  }

  public set root(value: Element | undefined) {
    this._root = value && value.nodeType === 1 ? value : undefined;
  }

  public get threshold(): number | number[] | undefined {
    return this._threshold;
  }

  /* istanbul ignore next */
  public set threshold(value: number | number[] | undefined) {
    let threshold: number[] = [];
    const isValidThreshold = (val: any): val is number =>
      typeof val === 'number' && val >= 0 && val <= 1;
    if (isValidThreshold(value)) {
      threshold = [value];
    } else if (Array.isArray(value) && value.length) {
      threshold = value.filter((val) => isValidThreshold(val));
    }
    if (threshold.length === 0) {
      threshold = [...DEFAULT_INTERSECTION_THRESHOLD];
    }
    this._threshold = threshold;
  }
}
